import { create } from 'zustand'
import type { MembershipDto, MeResponse, OrgRole, UserDto } from '@/lib/dto'
import { orgStore, tokenStore } from '@/lib/api'

/**
 * Сессия панели. Access-токен здесь **не** хранится — он живёт в памяти
 * `lib/api.ts` (US-1 AC1). Здесь только то, что рисует UI: пользователь,
 * организации, роль и права.
 */

export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous'

interface SessionState {
  status: SessionStatus
  user: UserDto | null
  memberships: MembershipDto[]
  activeOrgId: string | null
  permissions: string[]
  setSession: (me: MeResponse) => void
  clear: () => void
  setActiveOrg: (orgId: string) => void
}

export const useSessionStore = create<SessionState>(set => ({
  status: 'unknown',
  user: null,
  memberships: [],
  activeOrgId: null,
  permissions: [],
  setSession: me => {
    orgStore.set(me.activeOrgId)
    set({
      status: 'authenticated',
      user: me.user,
      memberships: me.memberships,
      activeOrgId: me.activeOrgId,
      permissions: me.permissions
    })
  },
  clear: () => {
    tokenStore.clear()
    orgStore.set(null)
    set({
      status: 'anonymous',
      user: null,
      memberships: [],
      activeOrgId: null,
      permissions: []
    })
  },
  setActiveOrg: orgId => {
    orgStore.set(orgId)
    set({ activeOrgId: orgId })
  }
}))

/** Роль в активной организации. */
export function activeRole(state: {
  memberships: MembershipDto[]
  activeOrgId: string | null
}): OrgRole | null {
  const membership = state.memberships.find(item => item.orgId === state.activeOrgId)
  return membership?.role ?? null
}

/**
 * US-6 AC3: VIEWER не может вызывать mutation-эндпоинты, и кнопки в UI
 * задизейблены с подсказкой. Одно место, где это решается.
 */
export function canOperate(role: OrgRole | null): boolean {
  return role === 'OWNER' || role === 'ADMIN' || role === 'OPERATOR'
}

export function useCanOperate(): boolean {
  const memberships = useSessionStore(state => state.memberships)
  const activeOrgId = useSessionStore(state => state.activeOrgId)
  return canOperate(activeRole({ memberships, activeOrgId }))
}

/**
 * Право `members:manage` (OWNER/ADMIN) — им же закрыты алерты (docs/06 §7.1).
 *
 * Считаем по роли, а не по `permissions[]` из `/auth/me`: строка права нужна
 * серверному guard'у, а на фронте единственный уже существующий источник
 * истины — роль в активной организации (см. `canOperate`). Смешивать два
 * механизма ради одного экрана — способ получить кнопку, которая у половины
 * пользователей всегда отдаёт 403.
 *
 * Экран алертов от этого не просто «дизейблит кнопки», а скрывается целиком:
 * правило хранит адрес доставки уведомлений, и пускать туда OPERATOR'а нельзя
 * даже на чтение.
 */
export function canManageOrgMembers(role: OrgRole | null): boolean {
  return role === 'OWNER' || role === 'ADMIN'
}

export function useCanManageOrgMembers(): boolean {
  const memberships = useSessionStore(state => state.memberships)
  const activeOrgId = useSessionStore(state => state.activeOrgId)
  return canManageOrgMembers(activeRole({ memberships, activeOrgId }))
}
